/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import isEmpty from 'lodash.isempty';
import {useViewSubGroups} from '@ion/applications/src/hooks/useViewSubGroups';
import {
  NAME,
  PREFERENCE,
  setDevicesViewSubGroups
} from '@ion/applications/src/pages/devices/state';
import {translate} from '@ion/core/src/locale/locale';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {ViewSubGroupsSwitch} from '@ion/dls/src/components/switch/ViewSubGroupsSwitch';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import Breadcrumb from '../components/breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '../components/breadcrumbs/BreadcrumbUtils';
import {useAnimatedMount} from '../hooks/useAnimatedMount';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useIsPhone} from '../hooks/useMedia';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import useThemeContext from '../theme/useThemeContext';
import {
  DLSTheme,
  PageViewSubGroupsProps,
  ViewSubGroupsPosition
} from '../types/types';
import DeviceDetailViewGrid from './grids/DeviceDetailViewGrid';
import styles, {getClasses} from './editorStyles';

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  closeButton : {
    [theme.breakpoints.down('sm')] : {
      left : '10px'
    }
  },
  paddingNone : {
    display                        : 'flex',
    flexDirection                  : 'column',
    height                         : 'calc(100vh - 332px)',
    padding                        : 0,
    overflowY                      : toggleThemeStyle('auto', 'hidden !important'),
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 284px)'
    }
  },
  deviceDetailGridBody : {
    overflowY                      : 'hidden',
    display                        : 'flex',
    flexDirection                  : 'column',
    [theme.breakpoints.down('sm')] : {
      padding : '0 20px'
    }
  },
  deviceDetailHeader : {
    margin         : '30px auto',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between'
  },
  subAccount : {
    marginRight : 'auto',
    paddingLeft : '35px'
  },
  label : {
    ...dlsTypography('h1'),
    fontSize      : '20px',
    textTransform : toggleThemeStyle('uppercase', 'capitalize')
  },
  chevron : {
    fill : dlsColor('grey3')
  },
  activeCrumb : {
    color         : dlsColor('grey1'),
    textTransform : toggleThemeStyle('none', 'capitalize')
  },
  inactiveCrumb : {
    color         : dlsColor('grey3'),
    textTransform : toggleThemeStyle('none', 'capitalize'),
    '&:hover'     : {
      cursor : 'pointer'
    }
  },
  formSection : {
    left           : '50%',
    color          : dlsColor('grey3'),
    margin         : 0,
    display        : 'flex',
    fontSize       : '20px',
    position       : 'absolute',
    transform      : 'translateX(-50%)',
    alignItems     : 'center',
    // the current theme's alias for its Archivo Narrow font files ...
    fontFamily     : 'Roboto',
    fontWeight     : 400,
    lineHeight     : '24px',
    whiteSpace     : 'nowrap',
    textTransform  : 'capitalize',
    justifyContent : 'center'
  },
  dialogTitle : {
    display    : 'flex',
    position   : 'relative',
    alignItems : 'center'
  },
  breadcrumbContainer : {
    display    : 'flex',
    alignItems : 'center'
  }
});

export interface DeviceDetailViewProps extends PageViewSubGroupsProps {
  onCloseDialog?: Function;
  onBreadcrumbBackClick?: Function;
  breadCrumbBackLabel?: string;
  isEditMode?: boolean;
  accountId?: any;
  isGreyOutGridRow?: Function;
  setDeviceDetailedRow?: Function;
  deviceDetailedSelectedRow?: any;
  includeDeviceGridCheckboxes?: boolean;
  getDeviceDetailedDoneBtnClick?: Function;
  singleRowSelection?: boolean;
  isCtcAdmin?: boolean;
  usePreferedPageSize?: boolean;
  manageSelect?: boolean;
  additionalSearchQuery?: string;
  hideSubGroups?: boolean;
  getDeviceSubGroupSwitchStatus?: (hasDeviceSubGroupSwitch: boolean) => void;
}

/**
 * @DeviceDetailView
 * The DeviceDetailView will be used to show the grid in the dialog
 * including search option.
 *
 * @param {Function} onCloseDialog Callback function fired when the dialog requests to be closed
 * @param {Function} onBreadcrumbBackClick Takes back to the place from where this dialog is was opened
 * @param {string} breadCrumbBackLabel Text to show for root/back breadcrumb
 * @param {boolean} isEditMode Checks the breadcrumb label whether opened from add/edit
 * @param {number} accountId Gets the selected group chooser account id
 * @param {Function} setDeviceDetailedRow Function used to set the selected row data
 * @param {Function} getDeviceDetailedDoneBtnClick Function used to check whether Submit button is clicked
 * @param {array} deviceDetailedSelectedRow Uses to get the selected row data
 */
export const DeviceDetailView = ({
  onCloseDialog,
  onBreadcrumbBackClick,
  breadCrumbBackLabel,
  isEditMode,
  accountId,
  isGreyOutGridRow,
  setDeviceDetailedRow,
  getDeviceDetailedDoneBtnClick,
  deviceDetailedSelectedRow,
  additionalSearchQuery,
  Features,
  getDeviceSubGroupSwitchStatus,
  manageSelect = false,
  includeDeviceGridCheckboxes = false,
  singleRowSelection = true,
  isCtcAdmin = false,
  usePreferedPageSize = false,
  enableSubgroupToggle = false,
  hideSubGroups = false
}: DeviceDetailViewProps) => {
  const {
    isCurrent : isCurrentTheme,
    isLegacy  : isLegacyTheme
  } = useThemeContext();

  const isPhone = useIsPhone();

  const editorStyles = useExtendStyles((theme) => styles(theme, isCurrentTheme), styleOverrides);

  const {
    isVisible,
    onRemove,
    timeout
  } = useAnimatedMount();

  const classes               = editorStyles();
  const singleDeviceSelection = !isEmpty(deviceDetailedSelectedRow) ? deviceDetailedSelectedRow?.asset : true;
  const isDoneBtnDisabled     = isCtcAdmin ? deviceDetailedSelectedRow?.length === 0 : singleDeviceSelection;
  const preference            = isCtcAdmin ? PREFERENCE : '';

  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups({
    name       : NAME,
    action     : setDevicesViewSubGroups,
    view       : !Features || enableSubgroupToggle,
    preference : preference
  });

  useEffect(() => {
    return () => {
      getDeviceDetailedDoneBtnClick?.(false);
      getDeviceSubGroupSwitchStatus?.(viewSubGroups);
    };
  }, [viewSubGroups]);

  const crumbClasses = {
    activeCrumb   : classes.activeCrumb,
    inactiveCrumb : classes.inactiveCrumb
  };

  const crumb1 = {
    label : breadCrumbBackLabel ||
      translate(`admin-assets:form.sections.${isEditMode ? 'edit' : 'add'}Asset`),
    click   : true,
    onClick : function(e = {}) {
      onBreadcrumbBackClick?.(e);
    }
  };

  const crumbData = defineBreadcrumbs({
    crumbs : [
      crumb1,
      {
        label : translate('common:editors.device.placeholder'),
        click : false
      }
    ],
    classes : crumbClasses
  });

  function handleSelect() {
    onClose(false);
    getDeviceDetailedDoneBtnClick?.(true);
    getDeviceSubGroupSwitchStatus?.(viewSubGroups);
  }

  function onClose(fromCloseBtn = true) {
    onRemove(onCloseDialog, fromCloseBtn);
    getDeviceDetailedDoneBtnClick?.(false);
    getDeviceSubGroupSwitchStatus?.(viewSubGroups);
  }

  const isGreyOutRow = useCallback((rowNode) => {
    return rowNode.data ? rowNode.data.asset : true;
  }, []);

  const toggleSubGroupsView = useCallback(() => {
    toggleViewSubGroups?.(!viewSubGroups);
  }, [viewSubGroups]);

  return (
    <Dialog disableEnforceFocus open
      transitionDuration={timeout}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={isCurrentTheme ? classes.dialogTitle : ''}>
          <div className={isCurrentTheme ? classes.breadcrumbContainer : ''}>
            <Breadcrumb icon={<ChevronLeft className={classes.chevron} />}
              height={'30px'}
              style={isCurrentTheme ? {alignItems: 'center'} : undefined}
              iconClick={onBreadcrumbBackClick}>
              {crumbData}
            </Breadcrumb>
          </div>
          <Render.If condition={isCurrentTheme && !isPhone}>
            <Typography component='h1' variant='h2' className={classes.formSection}>
              {translate(`${isCtcAdmin ? 'groups:form.sections.devices' : 'admin-devices:name'}`)}
            </Typography>
          </Render.If>
        </div>
      </DialogTitle>
      <div className={classes.deviceDetailGridBody}>
        <div className={classes.formWrapper}>
          <Render.If condition={isLegacyTheme}>
            <div className={classes.deviceDetailHeader}>
              <div className={classes.label}>
                {translate(`${isCtcAdmin ? 'groups:form.sections.devices' : 'admin-devices:name'}`)}
              </div>
              {hideSubGroups && (
                <div className={classes.subAccount}>
                  <ViewSubGroupsSwitch checked={viewSubGroups}
                    onChange={toggleSubGroupsView}
                    preference={PREFERENCE}
                    position={ViewSubGroupsPosition.GRID_CTAS}
                    labelColor={dlsColor('grey2')} />
                </div>
              )}
            </div>
          </Render.If>
          <DialogContent className={classes.paddingNone}>
            <DeviceDetailViewGrid accountId={accountId}
              isCtcAdmin={isCtcAdmin}
              isGreyOutRow={isGreyOutGridRow || isGreyOutRow}
              additionalSearchQuery={additionalSearchQuery}
              singleRowSelection={singleRowSelection}
              deviceDetailedSelectedRow={deviceDetailedSelectedRow}
              setDeviceDetailedRow={setDeviceDetailedRow}
              manageSelect={manageSelect}
              includeCheckboxes={includeDeviceGridCheckboxes}
              usePreferedPageSize={!isCtcAdmin && usePreferedPageSize}
              hideSubGroups={hideSubGroups}
              viewSubGroups={viewSubGroups}
              toggleViewSubGroups={toggleViewSubGroups} />
          </DialogContent>
        </div>
      </div>
      <DialogActions className={classes.formActions}>
        <TertiaryButton onClick={function(e) {
          onClose();
        }}>
          {translate('common:editors.shared.cancel')}
        </TertiaryButton>
        <div className={classes.primaryButton}>
          <PrimaryButton fullWidth disabled={isCtcAdmin ? false : Boolean(isDoneBtnDisabled)} onClick={handleSelect}>
            {translate('common:editors.shared.done')}
          </PrimaryButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceDetailView;
